import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../_shared/hooks';
import {
  initEventMembers,
  patchEventMember,
  patchEventMemberStatus,
  assignSeat,
  type EventMember,
} from '../event-manager-client';

export function useInitEventMembers(campaignId: string) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => initEventMembers(apiFetch, campaignId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', campaignId, 'event-members'] });
    },
  });
}

export function usePatchEventMember(campaignId: string) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, fields }: { memberId: number; fields: Parameters<typeof patchEventMember>[3] }) =>
      patchEventMember(apiFetch, campaignId, memberId, fields),
    onSuccess: (updated) => {
      queryClient.setQueryData<EventMember[]>(
        ['launcher', 'campaigns', campaignId, 'event-members'],
        (old) => old?.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)),
      );
    },
  });
}

export function usePatchEventMemberStatus(campaignId: string) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, status }: { memberId: number; status: string }) =>
      patchEventMemberStatus(apiFetch, campaignId, memberId, status),
    onSuccess: (_data, { memberId, status }) => {
      queryClient.setQueryData<EventMember[]>(
        ['launcher', 'campaigns', campaignId, 'event-members'],
        (old) => old?.map((m) => (m.id === memberId ? { ...m, cm_status: status } : m)),
      );
    },
  });
}

export function useAssignSeat(campaignId: string) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, tableId, seatPosition }: { memberId: number; tableId: number | null; seatPosition?: number | null }) =>
      assignSeat(apiFetch, campaignId, memberId, tableId, seatPosition),
    onSuccess: (updated) => {
      queryClient.setQueryData<EventMember[]>(
        ['launcher', 'campaigns', campaignId, 'event-members'],
        (old) => old?.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)),
      );
    },
  });
}
